import {
  Module,
  Mutation,
  VuexModule,
  getModule,
} from "vuex-module-decorators";

const name = "client"

import store from "@/store";
// Imported from an import-free leaf: the initializers below run during bootstrap, where a throw
// blanks the whole app. See the note in PersistedState.ts.
import {
  isBoolean,
  isStringArray,
  readPersistedJson,
  removePersisted,
  tabStorage,
  writePersistedJson,
} from "@/store/PersistedState";

/**
 * The three flags belong to the tab's Firebase session, so they live in the tab's own storage (#62):
 * a second tab signed in to another client must not overwrite them. Read from localStorage as well for
 * the tabs open across the release that moved them; written and cleared in the tab's storage, and the
 * old key is cleared too.
 */
function readTabFlag<T>(key: string, isValid: (value: unknown) => value is T): T | null {
  return readPersistedJson(key, isValid, tabStorage) ?? readPersistedJson(key, isValid);
}

if (store.state[name]) {
  store.unregisterModule(name);
}

@Module({ dynamic: true, namespaced: true, store, name: name })
export class ClientVuexModule extends VuexModule {
  ignoreIresAdminOrIresClientCheck = false;
  isAdministratorClientUser = readTabFlag('isAdministratorClientUser', isBoolean) ?? false;
  memberClientIds: string[] = readTabFlag('memberClientIds', isStringArray) ?? [];

  /**
   * Whether the signed-in client is an IRES admin-portal client.
   *
   * Mirrors `administration/adminConfig.officeAdminClientIds`, which can only be read
   * asynchronously. Settings, the navigation and the route guards all need the answer
   * *synchronously* while rendering, so the resolved value is cached here by
   * `resolveOfficeAdminPortalClient()` and persisted to the tab's storage, exactly like
   * `isAdministratorClientUser`. Persisting matters: without it every reload would render the
   * client-facing portal shape for a frame before the async read came back.
   *
   * Never the access boundary — the admin portal's real gate is the async check in
   * `router.beforeEach`. This only shapes what is offered.
   */
  isOfficeAdminPortalClient = readTabFlag('isOfficeAdminPortalClient', isBoolean) ?? false;

  @Mutation
  setIgnoreIresAdminOrIresClientCheck(val: boolean) {
    this.ignoreIresAdminOrIresClientCheck = val;
  }

  @Mutation
  setIsAdministratorClientUser(val: boolean) {
    this.isAdministratorClientUser = val;
    writePersistedJson('isAdministratorClientUser', val, tabStorage);
  }

  @Mutation
  setIsOfficeAdminPortalClient(val: boolean) {
    this.isOfficeAdminPortalClient = val;
    writePersistedJson('isOfficeAdminPortalClient', val, tabStorage);
  }

  @Mutation
  setMemberClientIds(val: string[]) {
    this.memberClientIds = val;
    writePersistedJson('memberClientIds', val, tabStorage);
  }

  @Mutation
  clear() {
    for (const key of ['isAdministratorClientUser', 'memberClientIds', 'isOfficeAdminPortalClient']) {
      removePersisted(key, tabStorage);
      removePersisted(key);
    }

    this.ignoreIresAdminOrIresClientCheck = false;
    this.isAdministratorClientUser = false;
    this.memberClientIds = [];
    this.isOfficeAdminPortalClient = false;
  }
}

export const ClientModule = getModule(ClientVuexModule);
